import { makeStyles, tokens } from '@fluentui/react-components';
import { Class24Regular } from '@fluentui/react-icons';

export interface ClassSummary {
  sectionId: string;
  sectionDisplayName?: string;
  classCode?: string;
  totalOperations: number;
  totalUsers: number;
  successCount: number;
  errorCount: number;
  warningCount: number;
  firstEvent?: string;
  lastEvent?: string;
}

interface ClassSummaryBarProps {
  summary: ClassSummary;
}

const useStyles = makeStyles({
  summaryBar: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    flexWrap: 'wrap',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    alignItems: 'center',
  },
  summaryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingRight: '16px',
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  summaryIcon: {
    color: tokens.colorBrandForeground1,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  summaryLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  summaryValue: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  successValue: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorValue: {
    color: tokens.colorPaletteRedForeground1,
  },
  warningValue: {
    color: tokens.colorPaletteYellowForeground1,
  },
});

const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR');
};

export const ClassSummaryBar: React.FC<ClassSummaryBarProps> = ({ summary }) => {
  const styles = useStyles();

  return (
    <div className={styles.summaryBar}>
      {/* Header com nome da classe */}
      <div className={styles.summaryHeader}>
        <Class24Regular className={styles.summaryIcon} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Classe</span>
          <span className={styles.summaryValue}>
            {summary.sectionDisplayName || summary.sectionId}
          </span>
        </div>
      </div>

      {/* Total de Operações */}
      <div className={styles.summaryItem}>
        <span className={styles.summaryLabel}>Total de Operacoes</span>
        <span className={styles.summaryValue}>{summary.totalOperations}</span>
      </div>

      {/* Usuários */}
      <div className={styles.summaryItem}>
        <span className={styles.summaryLabel}>Usuarios</span>
        <span className={styles.summaryValue}>{summary.totalUsers}</span>
      </div>

      {/* Sucesso */}
      <div className={styles.summaryItem}>
        <span className={styles.summaryLabel}>Sucesso</span>
        <span className={`${styles.summaryValue} ${styles.successValue}`}>
          {summary.successCount}
        </span>
      </div>

      {/* Erros */}
      <div className={styles.summaryItem}>
        <span className={styles.summaryLabel}>Erros</span>
        <span className={`${styles.summaryValue} ${styles.errorValue}`}>
          {summary.errorCount}
        </span>
      </div>

      {/* Avisos */}
      {summary.warningCount > 0 && (
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Avisos</span>
          <span className={`${styles.summaryValue} ${styles.warningValue}`}>
            {summary.warningCount}
          </span>
        </div>
      )}

      {/* Primeiro Evento */}
      <div className={styles.summaryItem}>
        <span className={styles.summaryLabel}>Primeiro Evento</span>
        <span className={styles.summaryValue}>{formatDateTime(summary.firstEvent)}</span>
      </div>

      {/* Último Evento */}
      <div className={styles.summaryItem}>
        <span className={styles.summaryLabel}>Ultimo Evento</span>
        <span className={styles.summaryValue}>{formatDateTime(summary.lastEvent)}</span>
      </div>
    </div>
  );
};

export default ClassSummaryBar;
